import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';

const buildAdminHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const devEmail = (import.meta.env as Record<string, string>)['VITE_DEV_ADMIN_EMAIL'];
  if (devEmail) headers['x-dev-admin-email'] = devEmail;
  return headers;
};

export function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [defaultRejectionReason, setDefaultRejectionReason] = useState('');

  const apiUrl = ((import.meta.env as Record<string, string>)['VITE_API_URL'] ?? '').replace(
    /\/+$/,
    ''
  );

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/admin/settings`, {
        headers: buildAdminHeaders(),
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data = (await res.json()) as { ok: boolean; settings: Record<string, string> };
      setDefaultRejectionReason(data.settings['default_rejection_reason'] ?? '');
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await fetch(`${apiUrl}/admin/settings`, {
        method: 'POST',
        headers: buildAdminHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          key: 'default_rejection_reason',
          value: defaultRejectionReason,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setSuccessMessage('Settings saved successfully.');
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure defaults for admin workflows.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Default rejection reason
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This text is pre-filled when an admin rejects a candidate. It can be edited per
            candidate at the time of rejection.
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={defaultRejectionReason}
            onChange={e => setDefaultRejectionReason(e.target.value)}
            placeholder="Enter the default rejection reason…"
            size="small"
          />

          <Divider sx={{ my: 2.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
